import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { CONTACT_PHONE_TEL } from '../config/contact';
import { REDUCED_MOTION_TRANSITION, SPRING_DEFAULT } from '../utils/springs';
import './About.css';

const HERO_IMAGE =
  '/images/timeline/2026/2026-05-23_56EFE65E-77AA-4C5A-9249-A557EA4F21E2.jpg';

const TIMELINE_YEARS = [
  '2018',
  '2019',
  '2020',
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
  '2026'
];

const TIMELINE_PHOTOS = {
  2018: [
    '/images/timeline/2018/2018-08-25_85C3F6FE-EFA1-410A-A87F-10C734C79DEB.jpg',
    '/images/timeline/2018/2018-06-16_413EB9DF-006C-4357-B5EA-330ADDCB96E5.jpg',
    '/images/timeline/2018/2018-07-15_77F8A1BC-14F6-43D6-BB65-C40D4D578B00.jpg',
    '/images/timeline/2018/2018-09-29_403FFDFB-62DE-4D69-A843-8EDA33954C02.jpg'
  ],
  2019: [
    '/images/timeline/2019/2019-06-22_5AC0F1EB-C297-4E10-9F9A-D8CADAF1280A.jpg',
    '/images/timeline/2019/2019-05-18_41DBB309-8B5B-4482-B682-29EB27F2B92F.jpg',
    '/images/timeline/2019/2019-06-21_0C02DC57-2577-4293-9733-4244D5B90B15.jpg',
    '/images/timeline/2019/2019-09-07_D030C65B-FE28-453E-8ECD-E5262F58B4F3.jpg'
  ],
  2020: [
    '/images/timeline/2020/2020-07-04_6100AF91-FFCD-460D-A8B8-37F8E8665B92.jpg',
    '/images/timeline/2020/2020-05-22_0369CFA7-D06D-441B-895A-5AF216CBF751.jpg',
    '/images/timeline/2020/2020-07-04_5B16C33E-C47B-4200-9B08-98FF70902055.jpg',
    '/images/timeline/2020/2020-09-20_FE3707CB-FB64-48C6-BAB3-B254DF84056C.jpg'
  ],
  2021: [
    '/images/timeline/2021/2021-04-10_2BB08E6A-2203-45FE-AF48-DAA0A1490D19.jpg',
    '/images/timeline/2021/2021-05-03_1B6014E4-BFD2-4C56-ACD8-3B6496209A02.jpg',
    '/images/timeline/2021/2021-08-12_3F502139-9E97-4AED-9043-1431816BA270.jpg',
    '/images/timeline/2021/2021-08-12_A654745F-0117-48FB-AC34-6A2084E02AEE.jpg'
  ],
  2022: [
    '/images/timeline/2022/2022-07-14_0957CBC5-00FE-4005-A4EB-0BBB0BF8D259.jpg',
    '/images/timeline/2022/2022-06-04_A91B2781-7AE4-4E74-B47D-81BB77F40B38.jpg',
    '/images/timeline/2022/2022-06-18_5E9A1ED3-72F9-4312-B354-86739C845886.jpg',
    '/images/timeline/2022/2022-07-14_97F9B373-8E45-4C9F-902F-7CAD1366B8C2.jpg'
  ],
  2023: [
    '/images/timeline/2023/2023-07-30_378BD306-56FE-4014-95DE-2CC15616F918.jpg',
    '/images/timeline/2023/2023-07-01_026A345D-348A-47BE-AAF7-57FF2D5546F4.jpg',
    '/images/timeline/2023/2023-07-30_B5227868-19D9-40A2-99B6-95BEFB92E20C.jpg',
    '/images/timeline/2023/2023-09-03_AC6712B1-10C1-4DE0-AB79-BC3479E73214.jpg'
  ],
  2024: [
    '/images/timeline/2024/2024-08-25_1B9EEAFD-5D51-49EE-90BE-D823F539BEE3.jpg',
    '/images/timeline/2024/2024-08-25_7B7E5A8B-A73B-476F-AF85-63467C427ED2.jpg',
    '/images/timeline/2024/2024-08-25_B12032B5-8997-4188-913A-78AC3ABD316E.jpg',
    '/images/timeline/2024/2024-08-25_04D5F4AB-2888-49E0-BF14-1EBC32477782.jpg'
  ],
  2025: [
    '/images/timeline/2025/2025-10-11_BFC3CB55-1C31-453D-B17F-0DFD18FE81D6.jpg',
    '/images/timeline/2025/2025-10-11_3AF9D9AA-5CEC-4040-9961-B6D2FDCF8875.jpg',
    '/images/timeline/2025/2025-10-11_BC2A3085-53F6-4315-A815-CDC1C0959716.jpg',
    '/images/timeline/2025/2025-11-06_37B268FC-EAC5-47F3-8035-F3906545AC2A.jpg'
  ],
  2026: [
    '/images/timeline/2026/2026-05-23_56EFE65E-77AA-4C5A-9249-A557EA4F21E2.jpg',
    '/images/timeline/2026/2026-05-23_5EF10AA5-0562-41F6-805F-D4F07AD8CBB9.jpg',
    '/images/timeline/2026/2026-08-02_655F8BEA-E1C0-4422-9366-1F6228ECA31C.jpg',
    '/images/timeline/2026/2026-09-05_A47F807F-0614-4A9D-AF0A-A4E8A50ABAD5.jpg'
  ]
};

const About = () => {
  const { t } = useLanguage();
  const reduceMotion = useReducedMotion();
  const cardTransition = reduceMotion ? REDUCED_MOTION_TRANSITION : SPRING_DEFAULT;
  const timeline = t('aboutPage.timeline');
  const heroPills = t('aboutPage.heroPills');

  const [activeYear, setActiveYear] = useState('2018');
  const [lightbox, setLightbox] = useState(null);
  const yearRefs = useRef({});
  const lightboxReturnFocusRef = useRef(null);
  const lightboxCloseRef = useRef(null);
  const lightboxPanelRef = useRef(null);

  const scrollToYear = (year) => {
    const el = document.getElementById(`timeline-year-${year}`);
    el?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    setActiveYear(year);
  };

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const entries = TIMELINE_YEARS.map((year) => {
      const node = yearRefs.current[year];
      if (!node) return null;
      return { year, node };
    }).filter(Boolean);

    if (!entries.length) return undefined;

    const observer = new IntersectionObserver(
      (observed) => {
        const visible = observed
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          const year = visible[0].target.id.replace('timeline-year-', '');
          setActiveYear(year);
        }
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0.15, 0.35, 0.55] }
    );

    entries.forEach(({ node }) => observer.observe(node));
    return () => observer.disconnect();
  }, [timeline]);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const openLightbox = (year, photoIndex) => {
    lightboxReturnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setLightbox({ year, photoIndex });
  };

  const stepLightbox = useCallback(
    (delta) => {
      setLightbox((current) => {
        if (!current) return current;
        const photos = TIMELINE_PHOTOS[current.year] || [];
        if (!photos.length) return current;
        const nextIndex = (current.photoIndex + delta + photos.length) % photos.length;
        return { ...current, photoIndex: nextIndex };
      });
    },
    []
  );

  const isLightboxOpen = lightbox !== null;

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = lightboxPanelRef.current;
    const returnEl = lightboxReturnFocusRef.current;
    const closeBtn = lightboxCloseRef.current;
    closeBtn?.focus();

    const focusable = panel?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLightbox();
        return;
      }
      if (event.key === 'ArrowLeft') {
        stepLightbox(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        stepLightbox(1);
        return;
      }

      if (event.key !== 'Tab' || !focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      if (returnEl instanceof HTMLElement && returnEl.isConnected) {
        returnEl.focus();
      }
    };
  }, [isLightboxOpen, closeLightbox, stepLightbox]);

  const lightboxPhotos = lightbox ? TIMELINE_PHOTOS[lightbox.year] || [] : [];
  const lightboxItem =
    Array.isArray(timeline) && lightbox
      ? timeline.find((item) => item.year === lightbox.year)
      : null;

  return (
    <div className="about-page">
      <section
        className="about-hero"
        style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
      >
        <div className="about-hero-overlay" aria-hidden="true" />
        <div className="container about-hero-inner">
          <p className="about-hero-eyebrow">{t('aboutPage.eyebrow')}</p>
          <h1 className="about-hero-title">{t('aboutPage.heroTitle')}</h1>
          <p className="about-hero-subtitle">{t('aboutPage.heroSubtitle')}</p>
          {Array.isArray(heroPills) && (
            <ul className="about-hero-pills">
              {heroPills.map((pill) => (
                <li key={pill}>{pill}</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <div className="timeline-scrubber-wrap">
        <nav
          className="timeline-scrubber"
          aria-label={t('aboutPage.scrubberLabel')}
        >
          <div className="timeline-scrubber-inner">
            {TIMELINE_YEARS.map((year) => (
              <button
                key={year}
                type="button"
                className={`timeline-scrubber-btn${
                  activeYear === year ? ' timeline-scrubber-btn--active' : ''
                }`}
                onClick={() => scrollToYear(year)}
                aria-current={activeYear === year ? 'true' : undefined}
              >
                {year}
              </button>
            ))}
          </div>
        </nav>
      </div>

      <section className="timeline-section">
        <div className="timeline-track">
          {Array.isArray(timeline) &&
            timeline.map((item, index) => {
              const photos = TIMELINE_PHOTOS[item.year] || [];
              const sideClass =
                index % 2 === 0 ? 'timeline-item--left' : 'timeline-item--right';

              return (
                <article
                  key={item.year}
                  id={`timeline-year-${item.year}`}
                  ref={(node) => {
                    yearRefs.current[item.year] = node;
                  }}
                  className={`timeline-item ${sideClass}`}
                >
                  <div className="timeline-node" aria-hidden="true">
                    <span>{item.year}</span>
                  </div>

                  <motion.div
                    className="timeline-card"
                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
                    whileInView={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={cardTransition}
                  >
                    <div className="timeline-card-header">
                      <span className="timeline-year-pill">{item.year}</span>
                      {Array.isArray(item.badges) &&
                        item.badges.map((badge) => (
                          <span key={badge} className="timeline-badge">
                            {badge}
                          </span>
                        ))}
                    </div>
                    <h2>{item.title}</h2>
                    <p className="timeline-story">{item.story}</p>

                    {photos.length > 0 && (
                      <div className="timeline-media-grid">
                        <button
                          type="button"
                          className="timeline-photo-main"
                          onClick={() => openLightbox(item.year, 0)}
                          aria-label={`${t('aboutPage.openPhotoLabel')} — ${item.title}`}
                        >
                          <img
                            src={photos[0]}
                            alt={`${item.alt} (1)`}
                            loading="lazy"
                            decoding="async"
                          />
                          <span className="timeline-photo-count">
                            {photos.length} {t('aboutPage.galleryCountLabel')}
                          </span>
                        </button>
                        <div className="timeline-photo-thumbs">
                          {photos.slice(1).map((src, thumbIndex) => (
                            <button
                              key={src}
                              type="button"
                              className="timeline-photo-thumb"
                              onClick={() => openLightbox(item.year, thumbIndex + 1)}
                              aria-label={`${t('aboutPage.openPhotoLabel')} — ${item.title}`}
                            >
                              <img
                                src={src}
                                alt={`${item.alt} (${thumbIndex + 2})`}
                                loading="lazy"
                                decoding="async"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </article>
              );
            })}
        </div>
      </section>

      <section className="about-cta-section">
        <div className="container">
          <div className="about-cta-card">
            <p className="about-cta-eyebrow">{t('aboutPage.cta.eyebrow')}</p>
            <h2>{t('aboutPage.cta.title')}</h2>
            <p className="about-cta-subtitle">{t('aboutPage.cta.subtitle')}</p>
            <div className="about-cta-badges">
              <span className="about-cta-badge about-cta-badge--cert">
                {t('aboutPage.cta.certBadge')}
              </span>
              <span className="about-cta-badge about-cta-badge--price">
                {t('aboutPage.cta.priceBadge')}
              </span>
            </div>
            <div className="about-cta-actions">
              <Link to="/products" className="btn-primary">
                {t('aboutPage.cta.primaryBtn')}
              </Link>
              <a href={`tel:${CONTACT_PHONE_TEL}`} className="about-phone-btn">
                {t('aboutPage.cta.phoneBtn')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && lightboxPhotos.length > 0 && (
          <motion.div
            className="timeline-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={lightboxItem?.title || lightbox.year}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={REDUCED_MOTION_TRANSITION}
            onClick={closeLightbox}
          >
            <div
              ref={lightboxPanelRef}
              className="timeline-lightbox-panel"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                ref={lightboxCloseRef}
                type="button"
                className="timeline-lightbox-close"
                onClick={closeLightbox}
                aria-label={t('aboutPage.lightboxClose')}
              >
                ×
              </button>
              <button
                type="button"
                className="timeline-lightbox-nav timeline-lightbox-nav--prev"
                onClick={() => stepLightbox(-1)}
                aria-label={t('aboutPage.lightboxPrev')}
              >
                ‹
              </button>
              <button
                type="button"
                className="timeline-lightbox-nav timeline-lightbox-nav--next"
                onClick={() => stepLightbox(1)}
                aria-label={t('aboutPage.lightboxNext')}
              >
                ›
              </button>
              <img
                src={lightboxPhotos[lightbox.photoIndex]}
                alt={
                  lightboxItem
                    ? `${lightboxItem.alt} (${lightbox.photoIndex + 1})`
                    : ''
                }
                className="timeline-lightbox-image"
              />
              <div className="timeline-lightbox-caption">
                <span>
                  {lightbox.year} · {lightboxItem?.title}
                </span>
                <span>
                  {lightbox.photoIndex + 1} / {lightboxPhotos.length}
                </span>
              </div>
              <div className="timeline-lightbox-thumbs">
                {lightboxPhotos.map((src, idx) => (
                  <button
                    key={src}
                    type="button"
                    className={`timeline-lightbox-thumb${
                      idx === lightbox.photoIndex ? ' is-active' : ''
                    }`}
                    onClick={() => setLightbox({ year: lightbox.year, photoIndex: idx })}
                    aria-label={`${t('aboutPage.openPhotoLabel')} ${idx + 1}`}
                  >
                    <img src={src} alt="" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default About;
